import { Meter as BaseMeter } from "@base-ui/react/meter";
import type React from "react";
import { type CSSProperties, forwardRef, useMemo } from "react";
import {
  type MeterState,
  meterIndicatorVariants,
  meterLabelClass,
  meterRootClass,
  meterTrackClass,
} from "./styles.css";

interface MeterProps {
  value: number;
  min?: number;
  max?: number;
  low?: number;
  high?: number;
  optimum?: number;
  label?: React.ReactNode;
  showValue?: boolean;
  className?: string;
  style?: CSSProperties;
}

export const Meter = forwardRef<HTMLDivElement, MeterProps>(function Meter(
  {
    value,
    min = 0,
    max = 100,
    low,
    high,
    optimum,
    label,
    showValue,
    className,
    style,
  },
  ref,
) {
  const state: MeterState = useMemo(() => {
    if (low !== undefined && high !== undefined && optimum !== undefined) {
      if (value < low || value > high) return "outOfRange";
      if (
        (optimum <= low && value <= low) ||
        (optimum >= high && value >= high)
      ) {
        return "suboptimal";
      }
    }
    return "optimal";
  }, [value, low, high, optimum]);

  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <BaseMeter.Root
      ref={ref}
      value={value}
      min={min}
      max={max}
      className={`${meterRootClass}${className ? ` ${className}` : ""}`}
      style={style}
    >
      {(label || showValue) && (
        <div className={meterLabelClass}>
          {label && <BaseMeter.Label>{label}</BaseMeter.Label>}
          {showValue && <BaseMeter.Value />}
        </div>
      )}
      <BaseMeter.Track className={meterTrackClass}>
        <BaseMeter.Indicator
          className={meterIndicatorVariants[state]}
          style={{ width: `${percentage}%` }}
        />
      </BaseMeter.Track>
    </BaseMeter.Root>
  );
});

Meter.displayName = "Meter";
